'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { House } from 'lucide-react'
import Image from 'next/image'
import { CHAT_ROUTE } from '@/lib/platform/chat-route'
import { FEEDBACK_ICON_SRC } from '@/lib/platform/feedback-icon-paths'
import '@/components/feedback/feedback-icon.css'
import '@/lib/notebook/styles/nb-feedback-chrome.css'
import { useNotebook } from '@/lib/notebook/core/store'
import { NotebookPublishMenu } from './NotebookPublishMenu'
import NotebookOptionsMenu from './NotebookOptionsMenu'

export default function TopBar() {
  const notebookId = useNotebook((s) => s.notebookId)
  const title = useNotebook((s) => s.title)
  const setTitle = useNotebook((s) => s.setTitle)
  const locked = useNotebook((s) => s.locked)
  const [draftTitle, setDraftTitle] = useState(title)
  const editingRef = useRef(false)

  useEffect(() => {
    if (!editingRef.current) setDraftTitle(title)
  }, [title])

  const commitTitle = (value) => setTitle(value)

  return (
    <motion.header
      className="nb-editor-chrome"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="nb-editor-chrome__left">
        <Link
          href="/home"
          className="nb-editor-chrome__home"
          aria-label="Home"
          title="Home"
          onPointerDown={() => {
            if (editingRef.current) {
              editingRef.current = false
              commitTitle(draftTitle)
            }
          }}
        >
          <House size={16} strokeWidth={1.75} aria-hidden />
        </Link>
        <span className="nb-editor-chrome__sep" aria-hidden>
          /
        </span>
        {locked ? (
          <span className="nb-editor-chrome__title nb-editor-chrome__title--preserved">
            {draftTitle || 'Untitled notebook'}
          </span>
        ) : (
          <input
            value={draftTitle}
            onFocus={() => {
              editingRef.current = true
            }}
            onBlur={() => {
              editingRef.current = false
              commitTitle(draftTitle)
            }}
            onChange={(e) => setDraftTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                e.currentTarget.blur()
              }
            }}
            placeholder="Untitled notebook"
            className="nb-editor-chrome__title"
          />
        )}
      </div>

      <div className="nb-editor-chrome__actions">
        <NotebookPublishMenu />

        {locked ? (
          <span
            className="nb-chrome-icon-btn nb-chrome-icon-btn--feedback is-disabled"
            aria-disabled="true"
            title="Preserved on your current plan"
          >
            <Image
              src={FEEDBACK_ICON_SRC}
              alt=""
              width={16}
              height={16}
              draggable={false}
              unoptimized
              className="nb-chrome-icon-btn__glyph feedback-icon feedback-icon--notebook"
              aria-hidden
            />
          </span>
        ) : (
          <Link
            href={CHAT_ROUTE}
            prefetch
            data-nb-chrome-feedback
            className="nb-chrome-icon-btn nb-chrome-icon-btn--feedback"
            aria-label="Open chat"
            title="Open chat"
          >
            <Image
              src={FEEDBACK_ICON_SRC}
              alt=""
              width={16}
              height={16}
              draggable={false}
              unoptimized
              className="nb-chrome-icon-btn__glyph feedback-icon feedback-icon--notebook"
              aria-hidden
            />
          </Link>
        )}

        <NotebookOptionsMenu disabled={locked} />
      </div>
    </motion.header>
  )
}
